import { CaseAppealListRow } from "../../../data-model/caseAppealsTable";
import { FilterValues } from "../../../data-model/filterValues";
import { CaseAppealsTableState } from "./useCaseAppealsTableData";
import { useFilterAndSearchEffect } from "./useFilterAndSearchEffect";
import { AppealListFilterPayload, useFilterPayload } from "./useFilterPayload";
import { usePaginationReset } from "./usePaginationReset";
import { useSearchTerm } from "./useSearchTerm";
import { useValidFilters } from "./useValidFilters";

export interface UseFilterAndSearchReturn {
  filterPayload: AppealListFilterPayload | null;
  searchTerm: string;
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  paginationResetCount: number;
  validFilters: FilterValues[];
}

/**
 * Coordinates Case Appeals filter/search state and refetching when either changes.
 *
 * The search term is sent to `pharmacy/appeal/list` as a predicate tree, so
 * matches come from the whole table rather than the loaded page.
 *
 * @param initialDataFetchingExecuted - Whether the first page has loaded
 * @param setTableData - Raw API rows setter
 * @param setCaseAppealsState - Table loading/error/data state setter
 * @param pageSize - Page size sent as `limit` to the appeal list API
 * @returns Search/filter state used by the appeals table
 */
export const useFilterAndSearch = (
  initialDataFetchingExecuted: boolean,
  setTableData: React.Dispatch<React.SetStateAction<CaseAppealListRow[]>>,
  setCaseAppealsState: React.Dispatch<
    React.SetStateAction<CaseAppealsTableState>
  >,
  pageSize: number = 50,
): UseFilterAndSearchReturn => {
  const { searchTerm, setSearchTerm } = useSearchTerm();
  const { paginationResetCount, setPaginationResetCount } =
    usePaginationReset();
  const validFilters = useValidFilters();

  const { filterPayload } = useFilterPayload(
    validFilters,
    searchTerm,
    initialDataFetchingExecuted,
    pageSize,
  );

  useFilterAndSearchEffect(
    filterPayload,
    initialDataFetchingExecuted,
    setTableData,
    setCaseAppealsState,
    setPaginationResetCount,
  );

  return {
    filterPayload,
    searchTerm,
    setSearchTerm,
    paginationResetCount,
    validFilters,
  };
};
